import type { Maybe, Nullable, SingleOrArray } from "@/types/utils";

export const valueIncludedOrEqual = <T>(
  compare: SingleOrArray<T>,
  value: Maybe<Nullable<T>>,
): value is T => {
  if (value !== null && value !== undefined) {
    return Array.isArray(compare)
      ? new Set(compare).has(value)
      : compare === value;
  }

  return false;
};

export const camelize = (str: string) => {
  return str
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) =>
      index === 0 ? word.toLowerCase() : word.toUpperCase(),
    )
    .replace(/\s+/g, "");
};

export const truncateString = (str: string, maxLength: number) => {
  if (str.length <= maxLength) return str;
  return str.slice(0, maxLength) + "...";
};

export const downloadBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.style.display = "none";
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  window.URL.revokeObjectURL(url);
  link.remove();
};

export const numberIsTruthyOrZero = (
  value?: Maybe<Nullable<number>>,
): value is number => !!(!isNaN(value ?? 0) && (value || value === 0));

export const mapArrayToProperty = <
  T extends object,
  Key extends keyof T = keyof T,
>(
  arr: T[],
  property: Key,
): T[Key][] => arr.map((el) => el[property]);

export const anyEmptyStrings = (first: string, ...rest: string[]) =>
  [first, ...rest].some((value) => !value.trim());

// Builds a string for the data-testid attribute following the format described
// in the README.md. Discriminator will typically be an index, but can be a
// single value or array for compound discriminators.
export const buildTestId = (
  page: string,
  container: string,
  element: string,
  discriminator?: SingleOrArray<string | number>,
) => new TestID(page, container, element, discriminator).id;

/**
 * TestID class for building structured test identifiers.
 *
 * In most cases `buildTestId` will be sufficient. This class is for edge cases
 * where the parent component needs to pass down a base test ID, but only has
 * portions of the required data. All methods return new instances (immutable).
 *
 * @example
 * ```typescript
 * // Create a base TestID
 * const baseTestId = new TestID("home", "table", "delete");
 *
 * // Add discriminators for specific instances
 * const specificTestId = baseTestId.addDiscriminator("user-123");
 * console.log(specificTestId.id); // "home__table__delete--user-123"
 *
 * // Chain discriminators
 * const chainedTestId = baseTestId
 *   .addDiscriminator("row")
 *   .addDiscriminator(2);
 * console.log(chainedTestId.id); // "home__table__delete--row--2"
 * ```
 */
export class TestID {
  private discriminator?: (string | number)[];

  /**
   * Creates a new TestID instance.
   *
   * @param page - The page identifier (e.g., "home", "settings")
   * @param container - The container identifier (e.g., "sidebar", "table")
   * @param element - The element identifier (e.g., "button", "input")
   * @param discriminator - Optional discriminator(s) to distinguish between similar elements
   */
  constructor(
    private page?: string,
    private container?: string,
    private element?: string,
    discriminator?: SingleOrArray<string | number>,
  ) {
    if (discriminator !== undefined) {
      this.discriminator = [discriminator].flat();
    }
  }

  private get testIdIsValid() {
    return !!this.page && !!this.container && !!this.element;
  }

  private get formattedId() {
    const baseId = [this.page, this.container, this.element].join("__");
    if (this.discriminator === undefined) {
      return baseId;
    }

    return `${baseId}--${[this.discriminator].flat().join("--")}`;
  }

  /**
   * Returns a new TestID instance with updated component parts.
   *
   * @param params - Object containing component parts to update
   * @param params.page - New page identifier (optional)
   * @param params.container - New container identifier (optional)
   * @param params.element - New element identifier (optional)
   * @param params.discriminator - New discriminator(s) to replace existing ones (optional)
   * @returns A new TestID instance with the updated components
   *
   * @example
   * ```typescript
   * const base = new TestID("home");
   * const complete = base.addComponent({
   *   container: "table",
   *   element: "button",
   *   discriminator: "delete"
   * });
   * console.log(complete.id); // "home__table__button--delete"
   * ```
   */
  addComponent({
    page,
    container,
    element,
    discriminator,
  }: {
    page?: string;
    container?: string;
    element?: string;
    discriminator?: SingleOrArray<string | number>;
  }) {
    return new TestID(
      page ?? this.page,
      container ?? this.container,
      element ?? this.element,
      discriminator ?? this.discriminator,
    );
  }

  /**
   * Returns a new TestID instance with an additional discriminator appended.
   *
   * @param newDiscriminator - The discriminator to append (string or number)
   * @returns A new TestID instance with the appended discriminator
   *
   * @example
   * ```typescript
   * const base = new TestID("home", "table", "row", "user");
   * const specific = base.addDiscriminator(123);
   * console.log(specific.id); // "home__table__row--user--123"
   * ```
   */
  addDiscriminator = (newDiscriminator: string | number) => {
    return new TestID(this.page, this.container, this.element, [
      ...(this.discriminator ?? []),
      newDiscriminator,
    ]);
  };

  /**
   * Gets the formatted test ID string.
   *
   * @returns The formatted test ID string, or empty string if invalid
   *
   * @example
   * ```typescript
   * const testId = new TestID("home", "table", "button", ["delete", 123]);
   * console.log(testId.id); // "home__table__button--delete--123"
   * ```
   */
  get id() {
    // This serves to catch the problem during development. As the test IDs are
    // for internal use only, there is no negative user impact if this fails.
    if (!this.testIdIsValid) {
      console.error(
        new Error(
          `Attempted to format invalid test ID. Outcome would be: "${this.formattedId}"`,
        ),
      );

      return "";
    }

    return this.formattedId;
  }
}

///////////////
/// WARNING ///
///////////////
/*
 * These functions should be used with caution when passing an error from the
 * server to the client. Ensure any properties that may contain credentials or
 * secrets are sanitized to avoid leaking sensitive data on the client side.
 */
export const serializeError = (error?: Error) =>
  error ? JSON.stringify(error, Object.getOwnPropertyNames(error)) : undefined;

export const deserializeError = (jsonError?: string) => {
  if (!jsonError) {
    return undefined;
  }

  try {
    const errorObject = JSON.parse(jsonError);

    const hasMessage = Object.hasOwn(errorObject, "message");
    const hasStack = Object.hasOwn(errorObject, "stack");
    const hasCause = Object.hasOwn(errorObject, "cause");

    if (!hasMessage && !hasCause) {
      throw new Error(
        "Attempted to deserialize error with no `message` or `cause` properties",
      );
    }

    const deserializedError = new Error(
      hasMessage ? errorObject.message : "Unknown Deserialized Error",
      hasCause ? { cause: errorObject.cause } : {},
    );

    if (hasStack) {
      deserializedError.stack = errorObject.stack;
    }

    return deserializedError;
  } catch (e) {
    if (e instanceof Error) {
      window?.newrelic?.noticeError(e);
    } else {
      window?.newrelic?.noticeError(
        new Error(
          "An unknown error occurred when attempting to deserialize an error",
        ),
      );
    }
  }
};
